/**
 * Where one argument of a step's tool comes from, picked from a list: the AI (an input of the custom
 * tool), a fixed value, or a field of another step's result, which makes this step wait for that one.
 * An argument left unset is not sent.
 */

import { useState } from 'react';
import type { ToolField } from '@/modules/back-testing/datasets/tools/tool-schema';
import { FieldInput, FieldSelect } from '@/modules/simulator/ui/field';
import type { CustomToolArg, CustomToolStep } from '@/types/custom-tool.types';
import { useCustomToolBuilder } from '../builder/builder-context';
import { candidateSources, fixedValueStart, withAiInput, withArg, type CustomToolDraft } from '../custom-tool-draft';
import { outputPaths } from '../output-paths';
import { FixedValue } from '../steps/fixed-value';
import { useStepName } from '../steps/step-name';

type Source = CustomToolArg['source'];

const SOURCE_LABEL: Record<Source, string> = { input: 'The AI passes it', value: 'Fixed value', step: 'Another step' };

const TYPED_PATH = '__typed__';

interface SourceProps<S extends Source> {
	step: CustomToolStep;
	field: ToolField;
	arg: Extract<CustomToolArg, { source: S }>;
}

const AiInput = ({ step, field, arg }: SourceProps<'input'>) => {
	const { draft, change } = useCustomToolBuilder();
	// By position: a name being edited can be blank, or the same as another input's for a moment.
	const picked = draft.inputs.findIndex((input) => input.name === arg.input);
	const pick = (position: string) =>
		change((current) => {
			const input = current.inputs[Number(position)];
			return input ? withArg(current, step.id, field.name, { source: 'input', input: input.name }) : current;
		});
	return (
		<FieldSelect
			aria-label={`${field.name} input`}
			value={picked < 0 ? '' : String(picked)}
			placeholder='Pick an input'
			options={draft.inputs.map((input, position) => ({ value: String(position), label: input.name || 'unnamed' }))}
			onChange={pick}
		/>
	);
};

const FromStep = ({ step, field, arg, sources }: SourceProps<'step'> & { sources: CustomToolStep[] }) => {
	const { change, stepTools } = useCustomToolBuilder();
	const stepName = useStepName();
	const source = sources.find((candidate) => candidate.id === arg.step);
	const paths = source ? outputPaths(stepTools.toolFor(source)?.outputSchema) : [];
	const [typing, setTyping] = useState(false);
	// Typed when asked for, or when the path is one the result's schema does not list.
	const typed = typing || paths.length === 0 || (arg.path !== '' && !paths.includes(arg.path));
	const set = (next: CustomToolArg, group?: string) => change((current) => withArg(current, step.id, field.name, next), group);

	return (
		<div className='flex flex-col gap-1.5'>
			<FieldSelect
				aria-label={`${field.name} step`}
				value={arg.step}
				options={sources.map((other) => ({ value: other.id, label: stepName(other) }))}
				onChange={(next) => {
					setTyping(false);
					set({ source: 'step', step: next, path: '' });
				}}
			/>
			{paths.length > 0 && (
				<FieldSelect
					aria-label={`${field.name} field`}
					value={typed ? TYPED_PATH : arg.path}
					placeholder='Pick a field of its result'
					options={[...paths.map((path) => ({ value: path, label: path })), { value: TYPED_PATH, label: 'Type a path' }]}
					onChange={(path) => {
						setTyping(path === TYPED_PATH);
						if (path !== TYPED_PATH) set({ ...arg, path });
					}}
				/>
			)}
			{typed && (
				<FieldInput
					aria-label={`${field.name} path`}
					placeholder='orders.0.id'
					className='font-mono'
					value={arg.path}
					onChange={(event) => set({ ...arg, path: event.target.value.trim() }, `path:${step.id}:${field.name}`)}
				/>
			)}
		</div>
	);
};

export const ArgumentRow = ({ step, field }: { step: CustomToolStep; field: ToolField }) => {
	const { draft, change } = useCustomToolBuilder();
	const arg = step.args[field.name];
	const sources = candidateSources(draft, step.id);
	const choices: Source[] = sources.length ? ['input', 'value', 'step'] : ['input', 'value'];

	const choose = (source: string) =>
		change((current: CustomToolDraft) => {
			if (source === 'input') return withAiInput(current, step.id, field);
			if (source === 'value') return withArg(current, step.id, field.name, fixedValueStart(field));
			if (source === 'step') return withArg(current, step.id, field.name, { source: 'step', step: sources[0].id, path: '' });
			return withArg(current, step.id, field.name, null);
		});

	return (
		<div role='group' aria-label={field.name} className='grid gap-1.5 @xl:grid-cols-[minmax(0,11rem)_10rem_minmax(0,1fr)] @xl:items-start @xl:gap-2'>
			<div className='flex min-w-0 flex-col @xl:pt-1.5'>
				<span className='truncate text-xs leading-4 text-ink'>
					<span className='font-mono'>{field.name}</span>
					{field.required && <span className='text-gray-800'> · required</span>}
				</span>
				{field.description && <span className='line-clamp-2 text-[11px] leading-4 text-gray-800'>{field.description}</span>}
			</div>
			<FieldSelect
				aria-label={`${field.name} source`}
				value={arg?.source ?? ''}
				unsetLabel='Not set'
				options={choices.map((source) => ({ value: source, label: SOURCE_LABEL[source] }))}
				onChange={choose}
			/>
			{arg?.source === 'input' && <AiInput step={step} field={field} arg={arg} />}
			{arg?.source === 'value' && (
				<FixedValue
					className='h-8 w-full max-w-none rounded-lg px-2.5 text-xs'
					field={field}
					value={arg.value}
					onChange={(value) => change((current) => withArg(current, step.id, field.name, { source: 'value', value }), `value:${step.id}:${field.name}`)}
				/>
			)}
			{arg?.source === 'step' && <FromStep step={step} field={field} arg={arg} sources={sources} />}
			{!arg && field.required && <p className='text-xs leading-4 text-red-600 @xl:pt-2'>The tool needs this.</p>}
		</div>
	);
};
